'use client';

import { Elements, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripeExpressCheckoutElementReadyEvent } from '@stripe/stripe-js';
import { CheckoutBuyerAddress, GetCheckoutV2Response } from 'types';
import { useGlobalState } from '@/state';
import { ActionType } from '@/state/types';
import Links from '@/lib/helpers/link-helper';
import useAnalytics from '@/lib/hooks/use-analytics';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { analyticsBookData } from 'sdk';

type UpdateCheckout = ({ buyerAddress }: { buyerAddress: CheckoutBuyerAddress; }) => Promise<GetCheckoutV2Response>;
interface ExpressCheckoutFormProps {
  checkoutData: GetCheckoutV2Response;
  updateCheckout: UpdateCheckout;
  onReady?: (event: StripeExpressCheckoutElementReadyEvent) => void;
}

function generateLineItems(checkoutData: GetCheckoutV2Response) {
  return [
    {
      name: 'Books',
      amount: checkoutData?.booksAmount - checkoutData?.discountAmount
    },
    {
      name: 'Shipping',
      amount: checkoutData?.shippingAmount
    },
    {
      name: 'Tax',
      amount: checkoutData?.taxAmount
    }
  ];
}

function ExpressCheckoutForm({ checkoutData, updateCheckout, onReady }: ExpressCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { dispatch } = useGlobalState();
  const { analyticEvents } = useAnalytics();
  const [paymentType, setPaymentType] = useState<string>('unknown');
  const pathname = usePathname();

  function showError({ message }: { message: string; }) {
    dispatch({
      type: ActionType.ADD_MODAL_DATA,
      payload: {
        title: 'Something went wrong',
        message: 'There was an error processing this payment. Please try again or reach out to our support team if the issue continues.',
        showDismiss: true,
        dismissText: 'Ok'
      }
    });
  }

  useEffect(() => {
    if (!!window && !!localStorage) {
      localStorage.setItem('checkoutAnalytics', JSON.stringify({
        value: parseFloat(((checkoutData?.booksAmount || 0) / 100).toFixed(2)),
        shipping: parseFloat(((checkoutData?.shippingAmount || 0) / 100).toFixed(2)),
        item_count: checkoutData?.bookIds?.length,
        products: checkoutData?.books?.map(book => analyticsBookData(book)),
        checkoutType: 'express',
        paymentType,
        pathname,
        content_type: 'product', // for fb pixel
        content_ids: checkoutData?.bookIds // for fb pixel
      }));
    }
  }, [checkoutData, paymentType, pathname]);

  if (!window?.location?.origin) return null;

  return (
    <ExpressCheckoutElement
      id='express-checkout'
      onReady={onReady}
      onShippingAddressChange={async ({ resolve, reject, address }) => {
        try {
          const checkoutAddress: Partial<CheckoutBuyerAddress> = {
            city_locality: address?.city,
            state_province: address?.state,
            postal_code: address?.postal_code,
            country_code: address?.country
          };
          const updatedCheckoutData = await updateCheckout({ buyerAddress: checkoutAddress as CheckoutBuyerAddress });

          const { error } = await elements.fetchUpdates();
          if (error) throw new Error(error.message);

          resolve({
            lineItems: generateLineItems(updatedCheckoutData)
          });
        } catch (e: any) {
          console.error('Tax calculation error', e);
          showError({ message: e.message });
          reject();
        }
      }}
      onConfirm={async event => {
        try {
          const { error: submitError } = await elements.submit();
          if (submitError) throw new Error(submitError.message);

          const { error: confirmError } = await stripe.confirmPayment({
            elements,
            clientSecret: checkoutData.clientSecret,
            confirmParams: {
              return_url: window.location.origin + Links.payment.index.pathname
            },
          });
          if (confirmError) throw new Error(confirmError.message);
        } catch (e: any) {
          console.error('Confirm payment error', e);
          showError({ message: e.message });
        }
      }}
      onClick={({ resolve, expressPaymentType }) => {
        analyticEvents.generalTrack('Express Checkout Started', {
          expressPaymentType,
          pathname
        });
        setPaymentType(expressPaymentType);
        resolve({
          emailRequired: true,
          allowedShippingCountries: ['US'],
          shippingAddressRequired: true,
          shippingRates: [{
            id: 'media-mail',
            displayName: 'Standard Shipping',
            amount: checkoutData?.shippingAmount,
            deliveryEstimate: {
              maximum: { unit: 'day', value: 8 },
              minimum: { unit: 'day', value: 2 },
            },
          }],
          lineItems: generateLineItems(checkoutData)
        });
      }}
    />
  );
}

interface ExpressCheckoutProps {
  checkoutData: GetCheckoutV2Response;
  stripePromise: Promise<Stripe>;
  updateCheckout: UpdateCheckout;
  onReady?: (event: StripeExpressCheckoutElementReadyEvent) => void;
}
export default function ExpressCheckout({ checkoutData, stripePromise, updateCheckout, onReady }: ExpressCheckoutProps) {

  if (!checkoutData?.clientSecret || !window?.location?.origin) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: checkoutData.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            borderRadius: '12px'
          }
        }
      }}
    >
      <ExpressCheckoutForm checkoutData={checkoutData} updateCheckout={updateCheckout} onReady={onReady} />
    </Elements>
  );
}